import { useFormik } from "formik";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { TextInput } from "../../../components/inputs/textInput";
import { InfoComponent } from "../../../components/notification/infoComponent";
import { AuthHeader } from "../../../components/typography/authHeader";
import { OrText } from "../../../components/typography/orText";
import { useActionExecutor } from "../../../customeHooks/useActionExecutor";
import { useSocialAuth } from "../../../customeHooks/useSocialAuth";
import { useLoginMutation } from "../../../redux/features/auth/auth.api";
import { authSliceActions } from "../../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../../redux/hooks";
import { resumeUrlKey } from "../../../utils/constants";
import { BaseApiErrorResponse } from "../../../utils/types";
import { SignInSchema } from "../../../utils/validations";

export const LoginPage = () => {
  const [searchParams] = useSearchParams();

  const { executeAction } = useActionExecutor();

  const [login, { isLoading, isError, error }] = useLoginMutation();

  const {
    loginWighGoogle,
    isGoogleAuthError,
    authenticatingWithGoogle,
    googleAuthError,
  } = useSocialAuth();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        email: searchParams?.get("email") ?? "",
        password: "",
      },
      validationSchema: SignInSchema,
      onSubmit: async (vals) => {
        const response = await login({
          email: vals.email,
          password: vals.password,
        });

        if (response.data?.status === "success") {
          dispatch(
            authSliceActions.signUserIn({
              profile: {
                ...response.data.data,
                isFirstLogin: false, // Cause an initial login would have happened immediately after email verification during signup
              },
              token: response.data.data.token,
            }),
          );

          executeAction(
            response.data.data._id,
            response.data.data.email,
            searchParams?.get("eventId") ?? "",
          );

          const resumeUrl = localStorage.getItem(resumeUrlKey);
          if (resumeUrl) {
            navigate(resumeUrl);
            localStorage.removeItem(resumeUrlKey);
          } else {
            navigate("/home");
          }
        } else if (
          (response.error as BaseApiErrorResponse)?.data?.message
            ?.toLowerCase()
            ?.trim() === "please activate your account"
        ) {
          navigate(
            `/signup?step=verify&email=${encodeURIComponent(values.email)}`,
          );
        }
      },
    });

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Login to your account"
        text={
          <span>
            Need a Tikket account?{" "}
            <Link
              to={{
                pathname: "/signup",
                search: `?email=${!errors.email ? values.email : ""}&eventId=${searchParams?.get("eventId") ?? ""}&action=${searchParams?.get("action") ?? ""}`,
              }}
              className="font-bold text-grey-900 underline underline-offset-2"
            >
              Sign up
            </Link>
          </span>
        }
      />

      <div className="mt-10 flex flex-col gap-5">
        {isError && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        {isGoogleAuthError && (
          <InfoComponent
            title="Error"
            text={
              (googleAuthError as unknown as BaseApiErrorResponse).data
                .message ?? "An error occurred!"
            }
            type="error"
          />
        )}

        <Button
          type="button"
          onClick={() => {
            loginWighGoogle();
          }}
          text="Continue with Google"
          variation="plain"
          icon="google"
          loading={authenticatingWithGoogle}
          disabled={authenticatingWithGoogle}
        />

        <OrText />

        <TextInput
          type="text"
          name="email"
          value={values.email}
          placeholder="e.g. example@email.com"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Email"
          errors={errors.email && touched.email ? errors.email : ""}
          autoFocus
          autoFocusOnMobile={false}
        />

        <TextInput
          type="password"
          name="password"
          value={values.password}
          placeholder="Enter your password"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Password"
          errors={errors.password && touched.password ? errors.password : ""}
        />

        <Link
          to={`/forgot-password${!errors.email ? `?email=${encodeURIComponent(values.email)}` : ""}`}
          className="text-sm font-bold text-grey-700"
        >
          Forgot Password?
        </Link>

        <Button
          type="submit"
          text="Log in"
          variation="primary"
          className="mt-5"
          loading={isLoading}
          disabled={isLoading}
        />
      </div>
    </form>
  );
};
